/**
 * R3-Phase-3 — viewer-only API client.
 *
 * STRICTLY ISOLATED from the operator app: this module must not
 * import `@/lib/api`, `@/lib/auth`, or any other internal module.
 * It speaks ONLY to the `/api/v/*` endpoints, attaches the viewer
 * CSRF header from the `eda_viewer_csrf` cookie, and never reads or
 * writes the internal `eda_session` cookie.
 */

const VIEWER_CSRF_COOKIE = "eda_viewer_csrf";

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const parts = document.cookie ? document.cookie.split("; ") : [];
  for (const p of parts) {
    const eq = p.indexOf("=");
    if (eq === -1) continue;
    if (p.slice(0, eq) === name) return decodeURIComponent(p.slice(eq + 1));
  }
  return null;
}

function authHeaders(method) {
  const h = { Accept: "application/json" };
  if (method && method.toUpperCase() !== "GET" && method.toUpperCase() !== "HEAD") {
    h["Content-Type"] = "application/json";
    const csrf = readCookie(VIEWER_CSRF_COOKIE);
    if (csrf) h["X-Viewer-CSRF"] = csrf;
  }
  return h;
}

export class ViewerSessionExpiredError extends Error {
  constructor(status) {
    super(`viewer session expired (${status})`);
    this.name = "ViewerSessionExpiredError";
    this.status = status;
  }
}

export class ViewerNotValidError extends Error {
  constructor() {
    super("invitation_not_valid");
    this.name = "ViewerNotValidError";
  }
}

async function request(method, path, body) {
  let resp;
  try {
    resp = await fetch(path, {
      method,
      credentials: "include",
      headers: authHeaders(method),
      body: body == null ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    throw new Error(`network error: ${err.message || err}`);
  }
  if (resp.status === 401 || resp.status === 403) {
    throw new ViewerSessionExpiredError(resp.status);
  }
  if (resp.status === 410 || resp.status === 404) {
    throw new ViewerNotValidError();
  }
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }
  if (resp.status === 204) return null;
  const ct = resp.headers.get("content-type") || "";
  if (ct.startsWith("application/json")) return await resp.json();
  return await resp.text();
}

export async function consumeMagicLink(token) {
  // No 401-mapping for consume: a missing cookie is normal on first
  // hit.  Only treat 410/404 as terminal-invalid.
  let resp;
  try {
    resp = await fetch(`/api/v/${encodeURIComponent(token)}/consume`, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
  } catch (err) {
    throw new Error(`network error: ${err.message || err}`);
  }
  if (resp.status === 410 || resp.status === 404) {
    throw new ViewerNotValidError();
  }
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }
  return await resp.json();
}

export function streamUrl(invitationId) {
  // Browsers automatically send the viewer cookie on the
  // <video src=…> element because it's same-origin to /api/v.
  return `/api/v/stream/${encodeURIComponent(invitationId)}`;
}

export async function sessionInfo() {
  return await request("GET", "/api/v/session/info");
}

export async function acknowledge() {
  return await request("POST", "/api/v/acknowledge");
}

export async function heartbeat(eventType, playbackPositionS) {
  return await request("POST", "/api/v/heartbeat", {
    event_type: eventType,
    playback_position_s:
      typeof playbackPositionS === "number" && Number.isFinite(playbackPositionS)
        ? playbackPositionS
        : null,
  });
}

export function heartbeatBeacon(eventType, playbackPositionS) {
  // Phase R7 Item 3 — beacon-friendly viewer-end-of-session ping.
  //
  // `navigator.sendBeacon` can't attach custom headers (no CSRF token),
  // so we POST to the dedicated `/api/viewer/telemetry/session-end`
  // endpoint which validates only the HttpOnly session cookie.  The
  // older `/api/v/heartbeat` route remains for in-flight heartbeats
  // where fetch() can attach the CSRF header.
  if (typeof navigator === "undefined" || !navigator.sendBeacon) return false;
  // We deliberately only forward the two event types the new endpoint
  // accepts.  Other event_types fall back to the legacy heartbeat path
  // (kept for backwards compat with `video_started`/`progress` pings).
  const allowed = eventType === "session_end" || eventType === "video_completed";
  try {
    const body = JSON.stringify({
      event_type: eventType,
      playback_position_s:
        typeof playbackPositionS === "number" && Number.isFinite(playbackPositionS)
          ? playbackPositionS
          : null,
    });
    const url = allowed
      ? (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")
        + "/api/viewer/telemetry/session-end"
      : "/api/v/heartbeat";
    return navigator.sendBeacon(
      url,
      new Blob([body], { type: "application/json" }),
    );
  } catch {
    return false;
  }
}

export async function endSession() {
  try {
    await request("POST", "/api/v/session/end");
  } catch {
    // best-effort
  }
}
